import {
  type PriceAndPackageListDTO,
  type PriceAndPackageNestedListDTO,
  type PriceInfo,
  type PriceInfoDTO,
  transformPrice,
} from "~/domains/pricing/api/PriceInfo";
import { occUrl } from "~/app/api/occ/occFetch";
import { ApiCallPriority, apiGET, apiPOST } from "~/app/api/apiCallsService";
import { BatchSizeProvider } from "~/app/api/batchSizeProvider";
import { toMapFn } from "~/common/utils/reducers";
import { getRuntimeConfig } from "~/common/config/runtimeConfig";

export interface PriceApiData {
  productCode: string;
  priceReference?: string;
  packCode?: string;
}

interface PriceQueueElement {
  resolve: (data: PriceInfo[]) => void;
  reject: (data: unknown) => void;
  data: PriceApiData;
}

const generatePriceApiKey = (data: PriceApiData) => `${data.productCode}-${data.priceReference}-${data.packCode}`;

const P_CACHE: Dict<Promise<PriceInfo[]>> = {};
const queuedPCalls: PriceQueueElement[] = [];
let pGroupingScheduled: boolean = false;
let pGroupingTimer: number | null = null;

const ACCOUNT_CONTEXT_REQUEST_ABORTED = "Price request superseded by account context change";

export function requestPriceDataSingle(data: PriceApiData) {
  const cacheKey = generatePriceApiKey(data);
  let cacheEntry = P_CACHE[cacheKey];

  if (!cacheEntry) {
    const queueElement: Partial<PriceQueueElement> = { data };
    cacheEntry = new Promise((resolve, reject) => {
      queueElement.resolve = resolve;
      queueElement.reject = reject;
    });
    const createdEntry = cacheEntry;
    createdEntry.finally(() => {
      if (P_CACHE[cacheKey] === createdEntry) {
        delete P_CACHE[cacheKey];
      }
    });
    const finalizedQueueElement = queueElement as PriceQueueElement;
    queuedPCalls.push(finalizedQueueElement);
    P_CACHE[cacheKey] = cacheEntry;

    if (!pGroupingScheduled) {
      pGroupingScheduled = true;
      pGroupingTimer = window.setTimeout(dispatchApiCall, getRuntimeConfig().batching.interval);
    }
  }

  return cacheEntry;
}

// BatchSizeProvider is stateful (tracks the escalating batch index); construct it lazily on first
// use so it observes the bootstrap-populated batching config rather than module-load defaults.
let pnaBatchSizeProvider: BatchSizeProvider | null = null;
const getPnaBatchSizeProvider = () => {
  if (!pnaBatchSizeProvider) {
    pnaBatchSizeProvider = new BatchSizeProvider(getRuntimeConfig().batching.pna.batches);
  }
  return pnaBatchSizeProvider;
};

function dispatchApiCall() {
  pGroupingTimer = null;
  const itemsToCall = queuedPCalls.slice(0, getPnaBatchSizeProvider().getBatchSize());
  queuedPCalls.splice(0, itemsToCall.length);

  pGroupingScheduled = false;

  if (itemsToCall.length === 0) {
    return;
  }

  if (!getRuntimeConfig().batching.pna.priceEnabled) {
    for (const item of itemsToCall) {
      const promise = apiFetchRegularPriceSingle(item.data.productCode, item.data.priceReference, item.data.packCode);
      promise.then(item.resolve, item.reject);
    }
  } else {
    const promise = apiFetchRegularPriceMultiple(itemsToCall.map((item) => item.data));
    promise.then(
      (data) =>
        itemsToCall.forEach((item) => {
          const fetchedData = data[item.data.productCode];
          item.resolve(fetchedData);
        }),
      (error) => itemsToCall.forEach((item) => item.reject(error)),
    );
  }

  if (queuedPCalls.length > 0) {
    pGroupingScheduled = true;
    pGroupingTimer = window.setTimeout(dispatchApiCall, getRuntimeConfig().batching.interval);
  }
}

/** Clears queued/in-flight legacy Price promises at an account-context boundary. */
export function invalidatePriceRequestCache(): void {
  if (pGroupingTimer !== null) {
    window.clearTimeout(pGroupingTimer);
    pGroupingTimer = null;
  }
  pGroupingScheduled = false;

  const queued = queuedPCalls.splice(0, queuedPCalls.length);
  queued.forEach((item) => item.reject(new Error(ACCOUNT_CONTEXT_REQUEST_ABORTED)));
  Object.keys(P_CACHE).forEach((cacheKey) => delete P_CACHE[cacheKey]);
}

export async function apiFetchRegularPriceSingle(
  productCode: string,
  priceReference: string = "",
  packCode?: string,
): Promise<PriceInfo[]> {
  const singleApiUrl = occUrl("/pna/getPrice");
  const params = new URLSearchParams();
  if (priceReference) {
    params.append("priceReference", priceReference);
  }
  if (packCode) {
    params.append("packCode", packCode);
  }
  const paramsString = params.toString();
  const finalUrl = `${singleApiUrl}/${productCode}${paramsString ? `?${paramsString}` : ""}`;

  const response = await apiGET(finalUrl, {}, ApiCallPriority.LOW);
  if (!response.ok) {
    throw new Error("Error fetching data");
  }

  const dto: PriceAndPackageListDTO = await response.json();
  return (dto.items ?? []).map(transformPrice);
}

const multiApiUrl = occUrl("/pna/getPriceList");
export async function apiFetchRegularPriceMultiple(data: PriceApiData[]): Promise<Record<string, PriceInfo[]>> {
  const body = data.map((item) => ({
    code: item.productCode,
    priceReference: item.priceReference,
    packCode: item.packCode,
    quantity: 1,
  }));
  const response = await apiPOST(
    multiApiUrl,
    {
      body: JSON.stringify({ products: body }),
      headers: {
        "content-type": "application/json",
      },
    },
    ApiCallPriority.LOW,
  );

  if (!response.ok) {
    throw new Error("Error fetching data");
  }

  const dto: PriceAndPackageNestedListDTO = await response.json();
  const transformedData = (dto.itemGroups ?? []).map((group) => transformList(group.items ?? []));

  // noinspection UnnecessaryLocalVariableJS
  const record = toMapFn(
    transformedData.filter((list) => list.length > 0),
    (list) => list[0].code,
  );
  return record;
}

const transformList = (items: PriceInfoDTO[]) => items.map(transformPrice);
